import { useId } from 'react';

import { Box, Button, Flex, Grid, Skeleton, useSlotRecipe } from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';

import { IconsLinear } from '@nocowanie/icons/linear';

import { SearchEngineDesktopV7Props } from './search-engine-desktop-v7.props';

import { defaultSearchEngineTranslation, GuestsPickerInputDataModel } from '../../../models';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';
import { GuestsPicker } from '../../molecules/guests-picker';
import { RangePicker, RangePickerDates } from '../../molecules/range-picker';
import { SearchEngineInstantReservationCheckbox } from '../../molecules/search-engine-instant-reservation-checkbox';
import { SearchLocation } from '../../molecules/search-location';

export const SearchEngineDesktopV7 = <T extends AutocompleteBaseItemModel>({
    setValue,
    getValues,
    register,
    watch,
    isSubmitted,
    onInputValueUpdated,
    translationData = defaultSearchEngineTranslation,
    autocompleteData,
    isWebComponentVersion = false,
    isMapSearch = false,
    showSkeleton = false,
    hideInstantReservationInput = false,
    withListing = false,
    locationPlaceholder,
    shouldOpenCalendar = false,
    ...props
}: SearchEngineDesktopV7Props<T>) => {
    const recipe = useSlotRecipe({
        key: 'searchEngineDesktop',
    });
    const styles = recipe();
    const locationId = useId();

    const { isLoading } = autocompleteData;

    const wrapperStyles = withListing
        ? styles.listingSearchWrapper
        : isMapSearch
          ? styles.mapSearchWrapper
          : styles.wrapper;

    return (
        <Flex flexDirection={'column'}>
            <Skeleton loading={showSkeleton} asChild>
                <Grid
                    className="search-engine-container"
                    css={wrapperStyles}
                    zIndex={shouldOpenCalendar ? 'modal' : undefined}
                    {...props}
                >
                    <SearchLocation
                        inputProps={{
                            id: locationId,
                            inputProps: {
                                ...register('location'),
                                css: {
                                    _placeholder: {
                                        color: locationPlaceholder ? 'gray.900' : undefined,
                                    },
                                    ...styles.locationInput,
                                },
                                'data-testid': 'search-engine-location-input',
                                ...(locationPlaceholder
                                    ? { placeholder: locationPlaceholder }
                                    : undefined),
                            },
                        }}
                        autocompleteData={autocompleteData}
                        setValue={setValue}
                        flexGrow={4}
                    />

                    <Box css={styles.separator} />

                    <Box flexGrow={3}>
                        <RangePicker
                            selectedDates={[
                                getValues('dates.checkInDate'),
                                getValues('dates.checkOutDate'),
                            ]}
                            onRangeChange={(dates: RangePickerDates) => {
                                if (
                                    dates[0] !== getValues('dates.checkInDate') ||
                                    dates[1] !== getValues('dates.checkOutDate')
                                ) {
                                    setValue('dates.checkInDate', dates[0]);
                                    setValue('dates.checkOutDate', dates[1]);

                                    onInputValueUpdated();
                                }
                            }}
                            inputProps={{
                                inputProps: {
                                    border: 'none',
                                    borderRadius: 0,
                                    h: 'var(--element-height)',
                                },
                                'data-testid': 'search-engine-range-picker',
                            }}
                            isMobileBrowser={false}
                            isWebComponent={isWebComponentVersion}
                            flexDates={{
                                withFlexDates: true,
                                onFlexDateSelect: (flexDays: number) => {
                                    setValue('flexDates', flexDays);
                                    onInputValueUpdated();
                                },
                                selectedFlexDays: watch('flexDates'),
                            }}
                            defaultIsOpen={shouldOpenCalendar}
                        />
                    </Box>

                    <Box css={styles.separator} />

                    <Box flexGrow={3}>
                        <GuestsPicker
                            inputData={getValues('guests')}
                            noArrow={true}
                            onInputDataChanged={(data: GuestsPickerInputDataModel) => {
                                const newGuests = {
                                    adultsCount: data.adultsCount || 1,
                                    childrenCount: data.childrenCount || 0,
                                    childrenAges: data.childrenAges
                                        ? data.childrenAges.filter(
                                              (age): age is number => age !== undefined,
                                          )
                                        : [],
                                };

                                if (!isEqual(newGuests, getValues('guests'))) {
                                    setValue('guests', newGuests);
                                    onInputValueUpdated();
                                }
                            }}
                            triggerProps={{
                                'data-testid': 'search-engine-guests-picker',
                                css: styles.guestPickerTrigger,
                            }}
                            isMobileBrowser={false}
                        />
                    </Box>

                    <Button
                        type="submit"
                        disabled={isLoading || isSubmitted}
                        variant={'solid'}
                        colorPalette={'brand'}
                        data-testid="search-engine-submit-button"
                        css={styles.submit}
                    >
                        <IconsLinear.Search fontSize={'xl'} />
                        {translationData.buttonSubmitLabel}
                    </Button>
                </Grid>
            </Skeleton>

            {!hideInstantReservationInput ? (
                <SearchEngineInstantReservationCheckbox
                    onInputValueUpdated={onInputValueUpdated}
                    setValue={setValue}
                    watch={watch}
                    withBackground={true}
                    {...styles.instantReservationCheckboxWrapper}
                />
            ) : null}
        </Flex>
    );
};
